import { dropTargetForElements } from '@atlaskit/pragmatic-drag-and-drop/adapter/element-adapter';
import { styled } from '@linaria/react';
import { Group, Skeleton, Stack } from '@mantine/core';
import dayjs from 'dayjs';
import { useEffect, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';
import type { OrganizationStatusesByProjectsAndBoards } from '../../../../../api/organization-client/organization-client.type';
import { useProcessMapping } from '../../../../../api/process-client/process-client.hooks';
import type { Mapping, Step, Workflow } from '../../../../../api/workflows-client/workflows-client.type';
import { DropShadowCard } from '../../../../../components/drop-shadow-card/drop-shadow-card';
import type { ResolvedPhaseLabels } from '../../../../../helpers/phase-labels/phase-labels.type';
import { normalizeStatusName } from '../../../../../helpers/subgroup-helpers/subgroup-helpers';
import {
  baseWhite,
  primaryBase,
  redBase,
  secondaryBase,
  skyDark,
  skyLighter,
  yellowBase,
} from '../../../../../styles/design-tokens';
import { Icon } from '../../../../../ui-library/icon/icon';
import { Text } from '../../../../../ui-library/typography/typography';
import {
  addStepToSteps,
  filterOrganizationStatusesByProjectsAndBoardsBySubprojectIds,
  getMappedAndUnmappedStatusesForWorkflow,
  getStepDisplayName,
  maybeRemoveMappedStatusesFromWorkflowSteps,
} from '../workflows.helpers';
import { WorkflowAction } from './reducer';
import { Status } from './statuses';

const DEFAULT_WORKFLOW_STEPS = ['Backlog', 'In Progress', 'In Review', 'In Test', 'Deployable', 'Done'];

export function WorkflowConfiguration({
  state,
  dispatch,
  statusesByProjectsAndBoards,
  isEditable,
}: {
  state: Workflow;
  dispatch: (action: WorkflowAction) => void;
  statusesByProjectsAndBoards: OrganizationStatusesByProjectsAndBoards | undefined;
  isEditable: boolean;
}) {
  const i18n = useTranslation();
  const statusesForWorkflowSubprojects = filterOrganizationStatusesByProjectsAndBoardsBySubprojectIds(
    statusesByProjectsAndBoards,
    state.subprojects,
  );
  const { mappedStatuses, unmappedStatuses } = getMappedAndUnmappedStatusesForWorkflow(
    state,
    statusesForWorkflowSubprojects,
  );

  // Fetch the workflow's resolved bucket labels. The hook is
  // disabled until the workflow has an id (e.g. "New Workflow" before
  // first save) so consumers see canonical i18n labels until persistence
  // exists. ProcessMapping.labels is shipped only when the endpoint is
  // workflow-scoped, which `useProcessMapping({ workflowId })` is.
  const { mapping } = useProcessMapping({
    workflowId: state.id,
    options: { enabled: !!state.id },
  });
  const resolvedLabels: ResolvedPhaseLabels | undefined = mapping?.labels;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden',
      }}
    >
      <Stack>
        <Group justify="space-between" style={{ paddingBottom: '16px' }}>
          <Text size="large" weight="bold">
            {i18n.t('data_management.workflows.workflow_map')}
          </Text>
          {unmappedStatuses.length > 0 ? (
            <Text weight="bold" color={redBase}>{`${i18n.t(
              'data_management.workflows.unmapped',
            )} ${i18n.t('common.status.statuses')}: (${unmappedStatuses.length})`}</Text>
          ) : mappedStatuses.length > 0 ? (
            <Group>
              <Text weight="bold" color={primaryBase}>
                {i18n.t('common.calibrated')}
              </Text>
              <Icon name="check_circle" size={24} color={primaryBase} />
            </Group>
          ) : null}
        </Group>
        <Group justify="space-between">
          <Text size="small" color={skyDark}>
            {mappedStatuses.length} {i18n.t('common.status.statuses')} {i18n.t('data_management.workflows.mapped')}
          </Text>
          {state.metadata_updated_at && (
            <Text size="small" color={skyDark}>
              {i18n.t('common.last_updated')}: {dayjs(state.metadata_updated_at).format('MMM D, YYYY [at] h:mm A')}
            </Text>
          )}
        </Group>
      </Stack>
      <div
        style={{
          flex: 1,
          display: 'flex',
          minHeight: 0,
          height: '100vh',
          overflow: 'auto',
          padding: '16px 8px',
          marginBottom: '32px',
          backgroundColor: skyLighter,
        }}
      >
        <WorkflowContainer>
          {DEFAULT_WORKFLOW_STEPS.map((stepName, index) => (
            <WorkflowStep
              key={stepName}
              stepName={stepName}
              ordinalPosition={
                index === 0 ? 'first' : index === DEFAULT_WORKFLOW_STEPS.length - 1 ? 'last' : 'internal'
              }
              state={state}
              dispatch={dispatch}
              isEditable={isEditable}
              step={(state.steps || []).find((step) => step.name === stepName)}
              resolvedLabels={resolvedLabels}
            />
          ))}
        </WorkflowContainer>
        <WorkflowContainer>
          <WorkflowStep
            stepName="Waiting"
            ordinalPosition="waiting"
            state={state}
            dispatch={dispatch}
            isEditable={isEditable}
            step={(state.steps || []).find((step) => step.name === 'Waiting')}
            resolvedLabels={resolvedLabels}
          />
        </WorkflowContainer>
      </div>
    </div>
  );
}

function WorkflowStep({
  stepName,
  ordinalPosition,
  step,
  isEditable,
  state,
  dispatch,
  resolvedLabels,
}: {
  stepName: string;
  ordinalPosition: 'first' | 'internal' | 'last' | 'waiting';
  step: Step | undefined;
  state: Workflow;
  dispatch: (action: WorkflowAction) => void;
  isEditable: boolean;
  resolvedLabels?: ResolvedPhaseLabels;
}) {
  const i18n = useTranslation();
  const ref = useRef<HTMLDivElement>(null!);
  // instead of boolean, use a string to record the name of the status that is being dragged over
  // null means no status is being dragged over
  const [draggedOver, setDraggedOver] = useState<string | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) {
      return;
    }

    const logDOMStructure = () => {
      const domStructure = [];
      let parentEl = el.parentElement;
      while (parentEl) {
        domStructure.push({
          tagName: parentEl.tagName,
          id: parentEl.id || 'no-id',
          className: parentEl.className || 'no-class',
        });
        parentEl = parentEl.parentElement;
      }
    };

    logDOMStructure();

    let cleanup: (() => void) | undefined;

    try {
      cleanup = dropTargetForElements({
        element: el,
        getData: () => {
          return { stepName };
        },
        onDragEnter: ({ source }: { source: { data: Record<string, any> } }) => {
          if (source.data.type === 'status') {
            setDraggedOver(source.data.status as string);
          }
        },
        onDragLeave: () => {
          setDraggedOver(null);
        },
        onDrop: ({
          source,
          location,
        }: {
          source: { data: Record<string, any> };
          location: { current: { dropTargets: any[] } };
        }) => {
          if (source.data.type !== 'status') {
            return;
          }

          // If the drop target is not found, do not allow the drop
          if (!location.current.dropTargets?.[0]) {
            return;
          }

          setTimeout(() => {
            try {
              // If the source status is found in another step, remove it from that step
              const newSteps = maybeRemoveMappedStatusesFromWorkflowSteps(state, [source.data.status as string]);

              // If the targeted step is not found, create a new step
              let targetedStep = (newSteps || []).find((step) => step && step.name === stepName);
              if (!targetedStep) {
                targetedStep = {
                  name: stepName as string,
                  mappings: [] as Mapping[],
                } as Step;
              }

              // If the source status is already in the target step, do not allow the drop to prevent duplicates.
              // Compared on the normalized name so a casing difference between
              // the drag payload and the persisted external_name still counts
              // as the same status.
              const normalizedDraggedStatus = normalizeStatusName(source.data.status as string);
              if (
                targetedStep.mappings.some(
                  (mapping) => normalizeStatusName(mapping.external_name) === normalizedDraggedStatus,
                )
              ) {
                return;
              }

              const uniqueId = `${Date.now()}-${source.data.status}`;

              targetedStep.mappings.push({
                external_name: source.data.status as string,
                id: uniqueId, // Add a unique ID to ensure proper rendering
              } as Mapping);

              dispatch({
                type: 'SET_WORKFLOW',
                payload: {
                  ...state,
                  steps: addStepToSteps(newSteps, targetedStep, ['Waiting', ...DEFAULT_WORKFLOW_STEPS]),
                },
              });

              setDraggedOver(null);
            } catch (error) {
              console.error('Error processing drop:', error);
            }
          }, 50); // Small delay to ensure drop event is fully processed
        },
      });
    } catch (error) {
      console.error('Error setting up drop target:', error, 'for step:', stepName);
    }

    return () => {
      if (cleanup) {
        try {
          cleanup();
        } catch (error) {
          console.error('Error cleaning up drop target:', error, 'for step:', stepName);
        }
      }
    };
  }, [state, dispatch, stepName]);

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <BucketIcon ordinalPosition={ordinalPosition} />
      <div style={{ height: '2px', width: '16px', backgroundColor: skyDark }} />
      {!['last', 'waiting'].includes(ordinalPosition) ? (
        <div
          style={{
            position: 'relative',
            top: '88px',
            right: '28px',
            height: '150px',
            width: '2px',
            borderLeft: `2px dashed ${secondaryBase}`,
          }}
        >
          <Icon
            name="keyboard_arrow_down"
            variant="filled"
            size={24}
            color={secondaryBase}
            style={{ position: 'relative', top: '140px', right: '13px' }}
          />
        </div>
      ) : null}
      <DropShadowCard
        ref={ref}
        style={{
          backgroundColor: baseWhite,
          border: draggedOver ? `2px solid ${primaryBase}` : 'none',
        }}
      >
        <WorkflowStepContainer>
          <Group justify="space-between">
            <Text
              weight="bold"
              style={{
                alignSelf: 'start',
                flex: 1,
                minWidth: 0,
                wordBreak: 'break-word',
              }}
            >
              {getStepDisplayName(stepName, i18n.t, resolvedLabels)}
            </Text>
          </Group>
          {step?.mappings && step.mappings.length ? (
            (step?.mappings || []).map((status) => (
              <Status key={status.id || status.external_name} status={status.external_name} isDraggable={isEditable} />
            ))
          ) : draggedOver ? (
            <Skeleton key={`drag-preview-${stepName}`} height={32} radius={8} />
          ) : (
            <EmptyState>Empty</EmptyState>
          )}
        </WorkflowStepContainer>
      </DropShadowCard>
    </div>
  );
}

function BucketIcon({ ordinalPosition }: { ordinalPosition: 'first' | 'internal' | 'last' | 'waiting' }) {
  if (ordinalPosition === 'first') {
    return <Icon name="play_circle_outline" variant="filled" size={24} color={primaryBase} />;
  }
  if (ordinalPosition === 'last') {
    return <Icon name="check_circle_outline" variant="filled" size={24} color={secondaryBase} />;
  }
  if (ordinalPosition === 'waiting') {
    return <Icon name="pause_circle_outline" variant="filled" size={24} color={yellowBase} />;
  }
  return <Icon name="adjust" variant="filled" size={24} color={secondaryBase} />;
}

const WorkflowStepContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-height: 100px;
  min-width: 150px;
  max-width: 150px;
  width: 150px;
  padding: 8px;
`;

const EmptyState = styled.div`
  padding: 4px 8px;
  border: 1px dashed ${skyDark};
  border-radius: 8px;
  display: flex;
  justify-content: center;
  align-items: center;
  justify-self: center;
  align-self: center;
`;

const WorkflowContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 50px;
  align-items: center;
  background-color: ${skyLighter};
  padding: 8px;
  height: fit-content;
`;
